/**
 * Metadados e formatação de moedas do Conversor / Pendência.
 */

export const CURRENCY_META = {
  BRL: {
    label: 'Real brasileiro',
    symbol: 'R$',
    flag: '🇧🇷',
    decimals: 2,
  },
  EUR: {
    label: 'Euro',
    symbol: '€',
    flag: '🇪🇺',
    decimals: 2,
  },
  USD: {
    label: 'Dólar americano',
    symbol: 'US$',
    flag: '🇺🇸',
    decimals: 2,
  },
  XOF: {
    label: 'Franco CFA ocidental',
    symbol: 'CFA',
    flag: '🇬🇼',
    decimals: 0,
  },
  XAF: {
    label: 'Franco CFA da África Central',
    symbol: 'FCFA',
    flag: '🇨🇲',
    decimals: 0,
  },
  AOA: {
    label: 'Kwanza angolano',
    symbol: 'Kz',
    flag: '🇦🇴',
    decimals: 2,
  },
  CVE: {
    label: 'Escudo cabo-verdiano',
    symbol: 'CVE',
    flag: '🇨🇻',
    decimals: 2,
  },
  CDF: {
    label: 'Franco congolês',
    symbol: 'FC',
    flag: '🇨🇩',
    decimals: 0,
  },
};

/** Moedas locais (sem BRL) para pendência na retirada. */
export const LOCAL_CURRENCY_CODES = Object.keys(CURRENCY_META).filter(
  (code) => code !== 'BRL'
);

export const CURRENCY_CODES = Object.keys(CURRENCY_META);

export const currencyOptionLabel = (code) => {
  const meta = CURRENCY_META[code];
  if (!meta) return code;
  return `${meta.flag} ${meta.label} (${code})`;
};

export const CURRENCY_OPTIONS = CURRENCY_CODES.map((code) => ({
  value: code,
  label: currencyOptionLabel(code),
}));

export const LOCAL_CURRENCY_OPTIONS = LOCAL_CURRENCY_CODES.map((code) => ({
  value: code,
  label: currencyOptionLabel(code),
}));

export const isZeroDecimalCurrency = (currency) =>
  (CURRENCY_META[currency]?.decimals ?? 2) === 0;

export const formatMoney = (value, currency) => {
  const n = Number(value) || 0;
  const meta = CURRENCY_META[currency];
  if (!meta) {
    return n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  }

  if (currency === 'BRL' || currency === 'EUR' || currency === 'USD') {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(n);
  }

  if (meta.decimals === 0) {
    return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n)} ${meta.symbol}`;
  }

  return `${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)} ${meta.symbol}`;
};

export const formatMoneyWithFlag = (value, currency) => {
  const meta = CURRENCY_META[currency];
  const formatted = formatMoney(value, currency);
  return meta?.flag ? `${meta.flag} ${formatted}` : formatted;
};

export const roundByCurrency = (value, currency) => {
  if (isZeroDecimalCurrency(currency)) return Math.round(value);
  return Math.round(value * 100) / 100;
};

export const maskAmountInput = (raw, currency) => {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (isZeroDecimalCurrency(currency)) {
    return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(
      Number(digits)
    );
  }
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(digits) / 100);
};

export const maskFromNumber = (value, currency) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return '';
  if (isZeroDecimalCurrency(currency)) {
    return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(
      Math.round(n)
    );
  }
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

export const parseMaskedAmount = (value, currency) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return null;
  if (isZeroDecimalCurrency(currency)) {
    const n = Number(digits);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(digits) / 100;
  return Number.isFinite(n) ? n : null;
};
